import type React from "react";
import { fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { Card, CardLabel } from "../visuals/Card";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { ACCENT_BLUE, ACCENT_ORANGE } from "../colors";

function DeviceMixSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const dt = data.methodology.device_types;
  const watch = dt.Watch;
  const bodyworn = dt.Bodyworn;
  const watchDevices = watch.device_count;
  const bodywornDevices = bodyworn.device_count;
  const total = watchDevices + bodywornDevices;

  const mixData: DonutDatum[] = [
    { name: "Falcon (Watch)", value: watchDevices, color: ACCENT_BLUE },
    { name: "Eagle (Bodyworn)", value: bodywornDevices, color: ACCENT_ORANGE },
  ].filter((d) => d.value > 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No device type data available for this period.
        </p>
      </div>
    );
  }

  const extendedRates = [
    { name: "Falcon", rate: watch.extended_rate, color: ACCENT_BLUE },
    { name: "Eagle", rate: bodyworn.extended_rate, color: ACCENT_ORANGE },
  ];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Tile
          label="Total Devices"
          value={total}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          resetKey={resetKey}
        />
        <Tile
          label="Falcon Sessions"
          value={watch.sessions}
          format={(v) => fmtInt(Math.round(v))}
          delay={200}
          accent={ACCENT_BLUE}
          resetKey={resetKey}
        />
        <Tile
          label="Eagle Sessions"
          value={bodyworn.sessions}
          format={(v) => fmtInt(Math.round(v))}
          delay={300}
          accent={ACCENT_ORANGE}
          resetKey={resetKey}
        />
        <Tile
          label="Avg Session (Falcon)"
          value={watch.avg_session_hours}
          format={(v) => `${v.toFixed(1)}h`}
          delay={400}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutWithLegend
          title="Device Mix"
          data={mixData}
          centerValue={total}
          centerLabel="devices"
          delay={500}
        />
        <Card delay={600}>
          <CardLabel>Extended Session Rate by Type</CardLabel>
          <div className="space-y-5 mt-2">
            {extendedRates.map((d, i) => (
              <div key={d.name}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm font-body">{d.name}</span>
                  <span className="font-head font-semibold text-white">
                    {d.rate.toFixed(1)}%
                  </span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(d.rate, 100)}%`,
                      background: d.color,
                      animation: `pmBarGrow 900ms ease-out ${600 + i * 150}ms both`,
                      transformOrigin: "left",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

export const deviceMixSlide: Slide = {
  id: "device-mix",
  title: "Device Mix",
  render: (ctx) => <DeviceMixSlideView {...ctx} />,
};
